import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { createSignPosting, updateSignPosting } from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import { getObjFromId } from '../../../../utils/directusFunctions';
import DateSelect from '../../../formElements/DateSelect';
import SingleSelect from '../../../formElements/SingleSelect';
import TextAreaField from '../../../formElements/TextAreaField';
import FormActions from '../../../ui/formActions/FormActions';

import { validate } from './validateSignPosting';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const _NOTES_LIMIT = 1000;

const EditSignPosting = ({ participant, onClose, isDisabled, formType, row = {} }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        participantId: '',
        date: todayDate(),
        organisationId: '',
        typeId: '',
        notes: ''
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [initialEntryState, setInitialEntryState] = useState(initialState);
    const [arrayTypes, setArrayTypes] = useState([]);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [errors, setErrors] = useState({});
    const msg = `Participant signposting has been ${formType === 'create' ? 'added' : 'updated'}`;

    // STORE STATE
    const signPostingOrganisations = useSelector(
        (state) => state.entities.directusService.signPostingOrganisations
    );

    // USE EFFECTS
    useEffect(() => {
        if (Object.keys(row).length < 1 || formType === 'create') return;
        row?.organisationId && configTypeArray(row.organisationId);
        const formattedRow = {
            ...row,
            notes: !row.notes ? '' : row.notes
        };
        setNewEntry(formattedRow);
        setInitialEntryState(formattedRow);
    }, [row]);

    useEffect(() => {
        if (arrayTypes?.length === 1)
            setNewEntry((prev) => ({
                ...prev,
                typeId: arrayTypes[0].id,
                type: arrayTypes[0].name
            }));
    }, [arrayTypes]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNS
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const configTypeArray = (organisationId) => {
        const newTypes = !organisationId
            ? []
            : signPostingOrganisations
                  .find((el) => el.id === organisationId)
                  ?.signposting_type.map((el) => el.signposting_type_id) || [];
        setArrayTypes(newTypes);
    };

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onOrganisationUpdate = (chosenId) => {
        clearError('organisationId');
        clearError('typeId');
        configTypeArray(chosenId);
        setNewEntry((prev) => ({
            ...prev,
            organisationId: chosenId,
            type: !chosenId ? '' : prev.type,
            typeId: !chosenId ? '' : prev.typeId
        }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, formType, initialEntryState);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = { ...newEntry, participantId: participant?.id };
        formType === 'create'
            ? dispatch(createSignPosting(payload, msg))
            : dispatch(updateSignPosting(payload.id, payload, msg));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form className={form.form} onSubmit={onSubmit} data-testid="edit_signposting_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Add New Signposting' : 'Edit Signposting'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <DateSelect
                            id="date"
                            label="Event Date"
                            value={newEntry.date}
                            disabled={isDisabled}
                            mandatory={true}
                            error={errors.date}
                            onDateChange={(date) => onUpdate('date', date)}
                        />
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="organisationId"
                            label="Signposted Organisation"
                            placeholder="Select a signposted organisation..."
                            mandatory={true}
                            disabled={isDisabled}
                            menuItems={signPostingOrganisations || []}
                            selectedId={newEntry.organisationId}
                            selected={getObjFromId(
                                signPostingOrganisations,
                                newEntry.organisationId
                            )}
                            error={errors.organisationId}
                            onChange={(chosenId) => onOrganisationUpdate(chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id="typeId"
                            label="Signposting Type"
                            placeholder="Select a signposting type..."
                            mandatory={true}
                            disabled={isDisabled || !newEntry.organisationId}
                            menuItems={arrayTypes || []}
                            selectedId={newEntry.typeId}
                            selected={getObjFromId(arrayTypes, newEntry.typeId)}
                            error={errors.typeId}
                            onChange={(chosenId) => onUpdate('typeId', chosenId)}
                        />
                    </div>
                </div>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <TextAreaField
                            id="notes"
                            label="Notes"
                            placeholder="Enter notes..."
                            disabled={isDisabled}
                            maxLength={_NOTES_LIMIT}
                            value={newEntry.notes}
                            count={`${newEntry.notes?.length}/${_NOTES_LIMIT}`}
                            error={errors.notes}
                            onChange={(e) => onUpdate('notes', e.target.value)}
                        />
                    </div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    disabled={isButtonDisabled || isDisabled}
                    customClass="noTopBorder"
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

EditSignPosting.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    formType: PropTypes.string,
    row: PropTypes.object
};

export default EditSignPosting;
